import React, { useEffect, useState } from 'react';
import '../styles/ContactSubmissions.css';

const STATUS_ACTIONS = [
  { value: 'read', label: 'Mark Read' },
  { value: 'replied', label: 'Mark Replied' },
  { value: 'archived', label: 'Archive' },
  { value: 'new', label: 'Mark New' },
];

const getStatusClass = (status) => {
  const safe = String(status || 'new').toLowerCase();
  return `contactSubmissionStatus contactSubmissionStatus--${safe}`;
};

const DetailRow = ({ label, children }) => (
  <div className="contactSubmissionDetailRow">
    <span className="contactSubmissionDetailLabel">{label}</span>
    <div className="contactSubmissionDetailValue">{children}</div>
  </div>
);

const ContactSubmissionDetailModal = ({
  submission,
  open,
  onClose,
  onStatusChange,
  onAddNote,
  updating,
  savingNote,
  formatDateTime,
}) => {
  const [noteText, setNoteText] = useState('');

  useEffect(() => {
    if (open) {
      setNoteText('');
    }
  }, [open, submission?.id]);

  if (!open || !submission) return null;

  const notes = Array.isArray(submission.adminNotes) ? submission.adminNotes : [];
  const sortedNotes = [...notes].sort((a, b) => {
    const aTime = new Date(a.createdAt || 0).getTime();
    const bTime = new Date(b.createdAt || 0).getTime();
    return bTime - aTime;
  });

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const handleStatusChange = (status) => {
    onStatusChange(submission.id, status);
  };

  const handleAddNote = async (event) => {
    event.preventDefault();
    const trimmed = noteText.trim();
    if (!trimmed || savingNote) return;

    const success = await onAddNote(submission.id, trimmed);
    if (success) {
      setNoteText('');
    }
  };

  return (
    <div
      className="contactSubmissionModalOverlay"
      onClick={handleOverlayClick}
      role="presentation"
    >
      <div
        className="contactSubmissionModal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="contactSubmissionModalTitle"
      >
        <div className="contactSubmissionModalHeader">
          <div>
            <h2 id="contactSubmissionModalTitle" className="contactSubmissionModalTitle">
              Contact Message
            </h2>
            <p className="contactSubmissionModalSubtitle">
              Submitted {formatDateTime(submission.createdAt)}
            </p>
          </div>
          <button
            type="button"
            className="contactSubmissionModalClose"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="contactSubmissionModalBody">
          <div className="contactSubmissionModalMeta">
            <span className={getStatusClass(submission.status)}>
              {submission.status || 'new'}
            </span>
            <span className={`contactSubmissionEmailSent contactSubmissionEmailSent--${submission.emailSent ? 'yes' : 'no'}`}>
              Email {submission.emailSent ? 'sent' : 'failed'}
            </span>
          </div>

          <DetailRow label="Name">
            {submission.name || '—'}
          </DetailRow>

          <DetailRow label="Email">
            {submission.email ? (
              <a className="contactSubmissionsLink" href={`mailto:${submission.email}`}>
                {submission.email}
              </a>
            ) : '—'}
          </DetailRow>

          <DetailRow label="Phone">
            {submission.phone ? (
              <a className="contactSubmissionsLink" href={`tel:${submission.phone}`}>
                {submission.phone}
              </a>
            ) : '—'}
          </DetailRow>

          <DetailRow label="Subject">
            {submission.subject || '—'}
          </DetailRow>

          <DetailRow label="Message">
            <div className="contactSubmissionsMessageBody">
              {submission.message || '—'}
            </div>
          </DetailRow>

          {submission.ip ? (
            <DetailRow label="IP Address">
              <span className="contactSubmissionsMutedText">{submission.ip}</span>
            </DetailRow>
          ) : null}

          <div className="contactSubmissionNotesSection">
            <h3 className="contactSubmissionNotesTitle">Internal Notes</h3>

            {sortedNotes.length === 0 ? (
              <p className="contactSubmissionsMutedText">No notes yet.</p>
            ) : (
              <div className="contactSubmissionNotesList">
                {sortedNotes.map((note) => (
                  <div key={note.id} className="contactSubmissionNoteItem">
                    <p className="contactSubmissionNoteText">{note.text}</p>
                    <p className="contactSubmissionNoteMeta">
                      {note.createdByName || 'Admin'} · {formatDateTime(note.createdAt)}
                    </p>
                  </div>
                ))}
              </div>
            )}

            <form className="contactSubmissionNoteForm" onSubmit={handleAddNote}>
              <textarea
                id="contactSubmissionNoteInput"
                className="contactSubmissionNoteInput"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Add an internal note for your team…"
                rows={3}
                aria-label="Add an internal note"
              />
              <button
                type="submit"
                className="contactSubmissionBtn contactSubmissionNoteSubmit"
                disabled={!noteText.trim() || savingNote}
              >
                {savingNote ? 'Saving…' : 'Add Note'}
              </button>
            </form>
          </div>
        </div>

        <div className="contactSubmissionModalFooter">
          <div className="contactSubmissionActions">
            {STATUS_ACTIONS.filter((action) => action.value !== submission.status).map((action) => (
              <button
                key={action.value}
                type="button"
                className="contactSubmissionBtn"
                disabled={updating}
                onClick={() => handleStatusChange(action.value)}
              >
                {action.label}
              </button>
            ))}
          </div>
          <button type="button" className="contactSubmissionModalCloseBtn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContactSubmissionDetailModal;
